import React, { useEffect, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { EmailOutlined, PriorityHigh } from '@mui/icons-material';
import { ActionItemKeys, ProviderActivationEventName } from '@uplift/api';
import { providerScheduleViewTypeRoute, ROUTING_CONFIG } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { L10nHTML, trackToDoCTA, useAllActionItems, useL10n } from '@uplift/hooks';
import { useProviderActivationEvents } from '@uplift/queries';
import { ProviderMedicalRole, UserRole } from '@uplift/types';
import {
  FocusedElement, FocusedElementTooltip, LinkDashboardActionItem, ModalContent, ModalTitle,
} from '@uplift/ui-v2';

import { useDashboardStyles } from '../styles';
import { isHighRisk, NewClientActionItem, NewPsychAppointment, NewPsychNote, PatientFilledFormActionItem, SetUp2fa, ToDosList } from './components';
import { VerifyAccountEmail } from './components/verify-email.component';

const inviteClientsLink = providerScheduleViewTypeRoute.createLink({}, { createAppointment: 'invite' });

const displayPriorities = {
  [ActionItemKeys.PROVIDER_INVITE_CLIENTS]: 1,
  [ActionItemKeys.PROVIDER_SET_UP_2FA]: 2,
  [ActionItemKeys.PROVIDER_UNSUBMITTED_SESSIONS]: 3,
  [ActionItemKeys.PROVIDER_VERIFY_ACCOUNT_EMAIL]: 4,
  [ActionItemKeys.PROVIDER_PATIENT_FILLED_ASSESSMENT_HIGH_RISK]: 5,
  [ActionItemKeys.PROVIDER_NEW_CLIENTS]: 6,
  [ActionItemKeys.PROVIDER_PATIENT_SCHEDULED_PSYCH]: 7,
  [ActionItemKeys.PROVIDER_NEW_PSYCH_NOTE]: 8,
  [ActionItemKeys.PROVIDER_PATIENT_FILLED_ASSESSMENT]: 9
};

export const ToDos: React.VFC = () => {
  const [ focusInviteClients, setFocusInviteClients ] = React.useState(false);
  const { classes } = useDashboardStyles();
  const { data: toDoActionItems, isLoading, dismiss } = useAllActionItems();
  const l10n = useL10n();
  const me = useCurrentuser();
  const { data: onboardingEvents } = useProviderActivationEvents(me.id);

  useEffect(() => {
    if (onboardingEvents) {
      const hasCompletedOnboarding = onboardingEvents?.some(event => event.eventName === ProviderActivationEventName.confettiDisplayed);

      if (hasCompletedOnboarding) {
        setFocusInviteClients(true);
      }
    }
  }, [ onboardingEvents ]);

  useEffect(() => {
    if (toDoActionItems.providerInviteClients?.show && focusInviteClients) {
      setTimeout(() => {
        window.scrollTo({ top: 250, behavior: 'smooth' });
      }, 300);
    }
  }, [ focusInviteClients, toDoActionItems.providerInviteClients?.show ]);

  const addInviteClientsToDo = useMemo(() => {
    const handleDismiss = () => {
      dismiss({ key: ActionItemKeys.PROVIDER_INVITE_CLIENTS });
    };

    const handleClick = () => {
      dismiss({ key: ActionItemKeys.PROVIDER_INVITE_CLIENTS });
      trackToDoCTA({
        toDoType: ActionItemKeys.PROVIDER_INVITE_CLIENTS,
        userType: UserRole.provider
      });
    };

    if (!toDoActionItems.providerInviteClients?.show) {
      return null;
    }

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_INVITE_CLIENTS],
      item: (
        <FocusedElement open={focusInviteClients}>
          <FocusedElementTooltip
            open={focusInviteClients}
            content={(
              <Box width="350px">
                <ModalTitle onClose={() => {
                  setFocusInviteClients(false);
                }}
                >{l10n.dashboard.actionItems.inviteClients.tooltipTitle}</ModalTitle>
                <ModalContent style={{ marginTop: '-2rem' }}>
                  <Typography style={{ paddingBottom: '0.5rem' }}>
                    {l10n.dashboard.actionItems.inviteClients.tooltipScheduleSessionsWithUpLift}
                  </Typography>
                  <L10nHTML variant="body1" color="textPrimary">
                    {l10n.dashboard.actionItems.inviteClients.tooltipInviteCurrentClient}
                  </L10nHTML>
                </ModalContent>
              </Box>
            )}
          >
            <Box display="flex">
              <LinkDashboardActionItem
                title={l10n.dashboard.actionItems.inviteClients.title}
                color="info"
                icon={<EmailOutlined />}
                link={inviteClientsLink}
                actionText="Invite Clients"
                dismiss={{
                  text: l10n.dashboard.actionItems.inviteClients.dismissText,
                  handleClick: handleDismiss
                }}
                onClick={handleClick}
              />
            </Box>
          </FocusedElementTooltip>
        </FocusedElement>
      )
    };
  }, [ toDoActionItems.providerInviteClients?.show, focusInviteClients, l10n.dashboard.actionItems, dismiss ]);

  const unsubmittedSessionsToDo = useMemo(() => {
    if (!toDoActionItems.providerUnsubmittedSessions?.unsubmittedSessionCount) {
      return null;
    }

    const unsubmittedCount = toDoActionItems.providerUnsubmittedSessions.unsubmittedSessionCount;

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_UNSUBMITTED_SESSIONS],
      item: (
        <LinkDashboardActionItem
          title={l10n.dashboard.actionItems.unsubmittedSessions.title(unsubmittedCount)}
          color="danger"
          icon={<PriorityHigh />}
          actionText={l10n.dashboard.actionItems.unsubmittedSessions.buttonText(unsubmittedCount)}
          link={ROUTING_CONFIG.notSubmittedSessions}
          onClick={() => trackToDoCTA({
            toDoType: ActionItemKeys.PROVIDER_UNSUBMITTED_SESSIONS,
            userType: UserRole.provider
          })}
        />
      )
    };
  }, [ l10n.dashboard.actionItems.unsubmittedSessions, toDoActionItems.providerUnsubmittedSessions ]);

  const firstPsychApptToDos = useMemo(() => {
    if (!toDoActionItems.providerPatientScheduledPsych?.clients) {
      return null;
    }

    if (!Array.isArray(toDoActionItems.providerPatientScheduledPsych?.clients)) {
      return null;
    }

    const sorted = [ ...toDoActionItems.providerPatientScheduledPsych.clients ]
      .sort((a, z) => a.nextTherapyAppointment.localeCompare(z.nextTherapyAppointment));

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_PATIENT_SCHEDULED_PSYCH],
      item: sorted.map(x => <NewPsychAppointment key={x.clientId} {...x} />)
    };
  }, [ toDoActionItems.providerPatientScheduledPsych ]);

  const newPsychNoteToDos = useMemo(() => {
    if (!toDoActionItems.providerNewPsychNote?.clients) {
      return null;
    }

    if (!Array.isArray(toDoActionItems.providerNewPsychNote?.clients)) {
      return null;
    }

    const sorted = [ ...toDoActionItems.providerNewPsychNote.clients ]
      .sort((a, z) => a.nextAppointment.localeCompare(z.nextAppointment));

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_NEW_PSYCH_NOTE],
      item: sorted.map(x => <NewPsychNote key={x.patientId} {...x} />)
    };
  }, [ toDoActionItems.providerNewPsychNote ]);

  const newClientsToDos = useMemo(() => {
    if (!toDoActionItems.providerNewClients?.clients) {
      return null;
    }

    if (!Array.isArray(toDoActionItems.providerNewClients?.clients)) {
      return null;
    }

    const sortedNewClients = [ ...toDoActionItems.providerNewClients.clients ]
      .sort((a, z) => a.appointmentTime.localeCompare(z.appointmentTime));

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_NEW_CLIENTS],
      item: sortedNewClients.map(x => (
        <NewClientActionItem
          appointmentId={x.appointmentId}
          appointmentTime={x.appointmentTime}
          firstName={x.firstName}
          lastName={x.lastName}
          fullName={x.fullName}
          userId={x.userId}
          key={x.userId}
          referral={x.referral}
          subsystem={x.subsystem}
          datetime={x.datetime}
        />
      ))
    };
  }, [ toDoActionItems.providerNewClients ]);

  const { highRisk, lowRisk } = useMemo(() => {
    if (!toDoActionItems.providerPatientFilledAssessment?.patients) {
      return { lowRisk: null, highRisk: null };
    }

    if (!Array.isArray(toDoActionItems.providerPatientFilledAssessment?.patients)) {
      return { lowRisk: null, highRisk: null };
    }

    const sortedClients = [ ...toDoActionItems.providerPatientFilledAssessment.patients ]
      .sort((a, z) => a.nextAppointmentAt.localeCompare(z.nextAppointmentAt));

    const lowRisk = {
      order: displayPriorities[ActionItemKeys.PROVIDER_PATIENT_FILLED_ASSESSMENT],
      item: sortedClients.filter(x => !isHighRisk(x)).map(x => <PatientFilledFormActionItem patient={x} key={x.patientId} />)
    };

    if (me.medicalRole === ProviderMedicalRole.psychiatrist) {
      return {
        lowRisk,
        highRisk: null
      };
    }

    const highRisk = {
      order: displayPriorities[ActionItemKeys.PROVIDER_PATIENT_FILLED_ASSESSMENT_HIGH_RISK],
      item: sortedClients.filter(x => isHighRisk(x)).map(x => <PatientFilledFormActionItem patient={x} key={x.patientId} />)
    };

    return {
      highRisk,
      lowRisk
    };
  }, [ me.medicalRole, toDoActionItems.providerPatientFilledAssessment?.patients ]);

  const setUp2faToDo = useMemo(() => {
    // there's an issue with the camel casing. 2FA -> 2Fa instead of 2fa as this seems to be expecting
    if (!toDoActionItems['providerSetUp2Fa']?.show) {
      return null;
    }

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_SET_UP_2FA],
      item: (
        <SetUp2fa userId={me?.id} />
      )
    };
  }, [ me?.id, toDoActionItems ]);

  const verifyAccountEmail = useMemo(() => {
    if (!toDoActionItems.providerVerifyAccountEmail?.show) {
      return null;
    }

    return {
      order: displayPriorities[ActionItemKeys.PROVIDER_SET_UP_2FA],
      item: (
        <VerifyAccountEmail />
      )
    };
  }, [ toDoActionItems ]);

  const toDos = useMemo(() => {
    const items = [
      addInviteClientsToDo,
      unsubmittedSessionsToDo,
      firstPsychApptToDos,
      newPsychNoteToDos,
      highRisk,
      newClientsToDos,
      lowRisk,
      setUp2faToDo,
      verifyAccountEmail
    ]
      .filter(x => x)
      .sort((a, b) => a.order - b.order)
      .flatMap(x => x.item);

    return items;
  }, [ addInviteClientsToDo, unsubmittedSessionsToDo, firstPsychApptToDos, newPsychNoteToDos, highRisk, newClientsToDos, lowRisk, setUp2faToDo, verifyAccountEmail ]);

  const title = (
    <Typography variant="h3">
      {l10n.dashboard.toDo.numberOfItems(toDos.length)}&nbsp;
      <span className={classes.secondary}>{l10n.dashboard.toDo.toDo}</span>
    </Typography>
  );

  return (
    <div style={{ marginBottom: 300 }}>
      <Box mb={3} ml={{ xs: 2, sm: 0 }}>
        {title}
      </Box>
      <ToDosList toDos={toDos} loading={isLoading} />
    </div>
  );
};
